import {Component, HostBinding, Input, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BaseFormGroup} from '../../../shared/utils/base-form-group';
import {ContactPerson} from '../../../../api/model/ContactPerson.model';
import {Contact} from '../../../../api/model/Contact.model';
import {ConfigService} from '../../../../core/services';
import {ContactType} from '../../../shared/enums/contact.type';
import {ContactValidationService} from '../../../shared/services/contact-validation.service';

@Component({
  selector: 'tj-single-contact-person-form',
  templateUrl: './single-contact-person-form.component.html',
  styleUrls: ['./single-contact-person-form.component.scss'],
})
export class SingleContactPersonFormComponent extends BaseFormGroup implements OnInit {
  @Input() contactPerson: ContactPerson;
  @Input() form: UntypedFormGroup;
  @Input() group: string;
  @Input() pdfReceiver = false;
  @Input() receiveNotificationsRequestedRep = false;
  @Input() receiveInvoiceRequestedRep = false;
  @Input() receivesCommentsIfRep = false;

  @HostBinding('class') classes = 'contact-persons-formGroup';
  formControls;

  constructor(
    private fb: UntypedFormBuilder,
    public configService: ConfigService,
    public contactValidationService: ContactValidationService,
  ) {
    super();
  }

  // Delegate to service for all validation-related methods
  get phoneNumberPlaceholder(): string {
    return this.contactValidationService.phoneNumberPlaceholder;
  }

  get phoneNumberErrorMessage(): string {
    return this.contactValidationService.phoneNumberErrorMessage;
  }

  get emailPlaceholder(): string {
    return this.contactValidationService.emailPlaceholder;
  }

  get emailErrorMessage(): string {
    return this.contactValidationService.emailErrorMessage;
  }

  get numberLabel(): string {
    return this.contactValidationService.numberLabel;
  }

  get emailLabel(): string {
    return this.contactValidationService.emailLabel;
  }

  get emailInputType(): string {
    return this.contactValidationService.emailInputType;
  }

  get textInputType(): string {
    return this.contactValidationService.textInputType;
  }

  getContactLabel(contactType: string): string {
    return this.contactValidationService.getContactLabel(contactType);
  }

  getContactPlaceholder(contactType: string): string {
    return this.contactValidationService.getContactPlaceholder(contactType);
  }

  getContactInputType(contactType: string): string {
    return this.contactValidationService.getContactInputType(contactType);
  }

  getContactErrorMessage(contactType: string): string {
    return this.contactValidationService.getContactErrorMessage(contactType);
  }

  getContactFieldProperties(contactType: string) {
    return this.contactValidationService.getContactFieldProperties(contactType);
  }

  getDynamicErrorMessage(contact: UntypedFormGroup): string {
    return this.contactValidationService.getDynamicErrorMessage(contact);
  }

  hasInvalidPhoneCharacters(value: string): boolean {
    return this.contactValidationService.hasInvalidPhoneCharacters(value);
  }

  hasInvalidPhoneCharactersForContact(contact: UntypedFormGroup): boolean {
    return this.contactValidationService.hasInvalidPhoneCharactersForContact(contact);
  }

  onKeyDown(event: KeyboardEvent, contactType: ContactType): void {
    this.contactValidationService.onKeyDown(event, contactType);
  }

  onPaste(event: ClipboardEvent, contact: UntypedFormGroup): void {
    this.contactValidationService.onPaste(event, contact);
  }

  onInput(event: Event, contact: UntypedFormGroup): void {
    this.contactValidationService.onInput(event, contact);
  }

  ngOnInit() {
    // Clear original values map to ensure clean state
    this.contactValidationService.clearOriginalValues();

    const contactPerson = this.contactPerson || ({} as ContactPerson);
    this.formControls = {
      id: this.fb.control(contactPerson.id || null),
      name: this.fb.control(contactPerson.name || null, Validators.required),
      notes: this.fb.control(contactPerson.notes || null),
      contacts: this.generateContactsFormArray(contactPerson.contacts),
      pdfReceiver: this.fb.control(this.pdfReceiver),
      receiveNotificationsRequestedRep: this.fb.control(this.receiveNotificationsRequestedRep),
      receiveInvoiceRequestedRep: this.fb.control(this.receiveInvoiceRequestedRep),
      receivesCommentsIfRep: this.fb.control(this.receivesCommentsIfRep),
      isMain: this.fb.control(true),
    };
    this.buildForm(this.form, this.formControls, this.group);
    this.defineGetters(this.formControls);
  }

  onRemoveContact(contactPersonIndex, contactIndex) {
    const contacts: UntypedFormArray = this.currentFormGroup.get('contacts') as UntypedFormArray;
    const contact = contacts.at(contactIndex) as UntypedFormGroup;

    // Clean up original values for the removed contact
    const contactId = contact.controls.id.value;
    const contactType = contact.controls.type.value;
    this.contactValidationService.removeOriginalValue(contactId, contactType);

    contacts.removeAt(contactIndex);
  }

  onAddContact() {
    const contacts: UntypedFormArray = this.currentFormGroup.get('contacts') as UntypedFormArray;
    contacts.push(this.generateContactControl(ContactType.EMAIL, '', null));
  }

  private generateContactsFormArray(contacts = []) {
    if (contacts.length === 0) {
      contacts = [
        {type: ContactType.EMAIL, value: ''} as Contact,
        {type: ContactType.MOBILE_PHONE, value: ''} as Contact,
      ];
    }
    return this.fb.array(
      contacts.map((contact: Contact) => {
        return this.generateContactControl(contact.type, contact.value, contact.id);
      }),
    );
  }

  private generateContactControl(type = null, value = '', id?) {
    const validators = this.contactValidationService.getValidatorsForContactType(type);

    const control = this.fb.group({
      id: this.fb.control(id),
      type: this.fb.control(type, Validators.required),
      value: this.fb.control(value, validators),
    });

    // Store original value for change detection
    this.contactValidationService.storeOriginalValue(id, type, value);

    // Set up value change detection
    this.contactValidationService.setupValueChangeDetection(control);

    return control;
  }

  /**
   * Changes the contact type and updates validation rules
   * @param event The type change event
   * @param contactIndex The index of the contact
   */
  changeContactType(event: any, contactIndex: number) {
    const contacts: UntypedFormArray = this.currentFormGroup.get('contacts') as UntypedFormArray;
    const contact = contacts.at(contactIndex) as UntypedFormGroup;

    this.contactValidationService.updateContactType(contact, event.value);
  }
}
